import {
  type EtapeTraduction,
  type EtatEtape,
  NOYAU_CODES,
  type NoyauCode,
  noyauCodeSchema,
} from '@rosetta/shared';
import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { ServiceIndisponibleError, SessionExpireeError } from '../erreurs';
import { CODE_LANGUE, dateTahitiSchema, idSchema, lireLignes, texteLocaliseSchema } from './h2data';
import type { AppelHttp } from './http';
import { type CorrespondanceStatuts, annulerTache, classerStatut, estEnAttente } from './taches';

/**
 * The sub-tasks of a translation (#7): what the waiting screen reads to show the steps, and
 * what an interruption cancels.
 *
 * A Rosetta v5 launch creates sub-tasks in `h2_task`, linked to it by `h2_task_main_id`, one
 * per step: `rose_task_v5_analyse` ("Analyse du texte"), `rose_task_v4_detect_invariants`
 * ("Repérage des invariants"), then one `rose_task_v5_traduction` per noyau ("Traduction
 * <noyau>"). Each step has AI sub-tasks of its own, linked to it the same way. The reads are
 * database reads (no call to the AI).
 *
 * TO VALIDATE(#7): everything in this file comes from a read of devdb on 2026-10-07 and has
 * not been played against a real account: that the account of an end user may read `h2_task`
 * and `h2_tasktype` (otherwise the screen falls back to "en cours", see `lireEtapesOuRepli`),
 * that the sub-tasks of every level sit in `h2_task` under `h2_task_main_id`, that the title of
 * a `rose_task_v5_traduction` names its noyau after the title of the translation (otherwise the
 * order of creation decides, #13), and that a sub-task created after the read is caught by none
 * of the cancellations.
 */

export const TABLE_SOUS_TACHE = 'h2_task';

export const TYPE_ANALYSE = 'rose_task_v5_analyse';
export const TYPE_INVARIANTS = 'rose_task_v4_detect_invariants';
export const TYPE_TRADUCTION = 'rose_task_v5_traduction';
/**
 * The quality control of the launch (#13): its last step, one per launch (not per noyau), listed
 * from the start of the wait (#32) and created by Tahiti once the last translation is done.
 */
export const TYPE_CONTROLE = 'rose_task_v5_controle';

/** The names of the step types start with this: one `LIKE` read gets them all. */
const PREFIXE_TYPES = 'rose_task_v';

/** More than the steps of a translation (two, plus one per noyau) and their AI sub-tasks. */
const LIMITE_SOUS_TACHES = 200;

/** A refusal is "Tahiti does not let this account read that", never "the session expired". */
const refusLecture = (statut: number): Error =>
  statut === 401
    ? new SessionExpireeError()
    : new ServiceIndisponibleError(`Tahiti refused the reading of the sub-tasks (${statut})`);

/* ── Task types ──────────────────────────────────────────────────────────────────── */

export const ligneTypeTacheSchema = z.object({
  id: idSchema,
  physical_name: z.string().nullish(),
});

/** `h2_tasktype_id` of Tahiti -> its `physical_name`, for the Rosetta step types. */
export type CorrespondanceTypes = ReadonlyMap<string, string>;

/**
 * Reads the names of the Rosetta task types (`h2_tasktype`, `physical_name` starting with
 * `rose_task_v`): the identifiers differ from one environment to the next, so they are read and
 * never written in the code. The caller keeps the result for the session.
 */
export async function lireCorrespondanceTypes(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
): Promise<CorrespondanceTypes> {
  const lignes = await lireLignes(
    appel,
    config,
    jeton,
    {
      table: 'h2_tasktype',
      fields: ['id', 'physical_name'],
      where: ['physical_name', `${PREFIXE_TYPES}%`, 'LIKE'],
      limit: 100,
    },
    ligneTypeTacheSchema,
    refusLecture,
  );
  return new Map(
    lignes.flatMap((ligne) =>
      ligne.physical_name == null ? [] : [[ligne.id, ligne.physical_name] as const],
    ),
  );
}

/* ── Sub-tasks ───────────────────────────────────────────────────────────────────── */

export const ligneSousTacheSchema = z.object({
  id: idSchema,
  title: texteLocaliseSchema.nullish(),
  /** Status letter of `h2_task`, never interpreted alone (see `classerStatut`). */
  status: z.string().min(1),
  h2_tasktype_id: idSchema.nullish(),
  created_at: dateTahitiSchema.nullish(),
  updated_at: dateTahitiSchema.nullish(),
});
export type LigneSousTache = z.infer<typeof ligneSousTacheSchema>;

/** The sub-tasks of a task: those whose `h2_task_main_id` is its identifier. */
export async function lireSousTaches(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  parentId: string,
): Promise<LigneSousTache[]> {
  return lireLignes(
    appel,
    config,
    jeton,
    {
      table: TABLE_SOUS_TACHE,
      fields: ['id', 'title', 'status', 'h2_tasktype_id', 'created_at', 'updated_at'],
      where: ['h2_task_main_id', Number(parentId), '='],
      limit: LIMITE_SOUS_TACHES,
      lang: CODE_LANGUE,
    },
    ligneSousTacheSchema,
    refusLecture,
  );
}

/* ── Steps ───────────────────────────────────────────────────────────────────────── */

/**
 * The state of the step a sub-task stands for. "Terminé" is finished; "En attente" (`W`) is a
 * step Tahiti created and has not started; a sub-task that failed (`I`, `F`) or was cancelled
 * (`C`) is a step in failure (#13: the translation is then blocked and will not end on its own;
 * #7 showed it "en cours", on the hypothesis that the task of the translation would decide at the
 * next read, which devdb belied); every other label (`R`, `S`) is running.
 */
export function etatEtape(lettre: string, correspondance?: CorrespondanceStatuts): EtatEtape {
  if (estEnAttente(lettre, correspondance)) return 'attente';
  const statut = classerStatut(lettre, correspondance);
  if (statut === 'terminee') return 'terminee';
  if (statut === 'echouee' || statut === 'interrompue') return 'echec';
  return 'en_cours';
}

/**
 * The code of a noyau as Tahiti puts it in the title of a translation sub-task, after the title
 * of the translation: "<titre> - SGP" or "<titre> - SGP - direct (texte court)" (devdb, 908222
 * and 910742). Upper case, between " - " and " - " or the end of the title.
 */
const CODE_APRES_TITRE = new RegExp(` - (${NOYAU_CODES.join('|')})(?= - |$)`, 'g');

/**
 * The noyau a translation sub-task names in its title, if any: the LAST code that stands as a
 * segment of its own (B1, #13). The title of the translation comes first and may hold a code
 * ("Panne du GPS de la flotte", "Le PSG en finale"): the first code found anywhere, case folded,
 * gave the step to the wrong noyau.
 */
export function noyauDuTitre(titre: string | null | undefined): NoyauCode | undefined {
  const trouves = [...(titre ?? '').matchAll(CODE_APRES_TITRE)];
  const code = noyauCodeSchema.safeParse(trouves.at(-1)?.[1]);
  return code.success ? code.data : undefined;
}

/** The most recent sub-task of a list (the highest identifier: a step that was rerun). */
function laPlusRecente(lignes: readonly LigneSousTache[]): LigneSousTache | undefined {
  return [...lignes].sort((a, b) => Number(b.id) - Number(a.id))[0];
}

function etape(
  base: Pick<EtapeTraduction, 'code' | 'libelle'> & { noyau?: NoyauCode },
  ligne: LigneSousTache | undefined,
  correspondance: CorrespondanceStatuts | undefined,
): EtapeTraduction {
  if (ligne === undefined) return { ...base, etat: 'attente' };
  const etat = etatEtape(ligne.status, correspondance);
  const debut = ligne.created_at == null ? Number.NaN : Date.parse(ligne.created_at);
  const fin = ligne.updated_at == null ? Number.NaN : Date.parse(ligne.updated_at);
  const duree = Math.round((fin - debut) / 1000);
  return {
    ...base,
    etat,
    // The duration of a finished step: its creation to its last update.
    ...(etat === 'terminee' && Number.isFinite(duree) ? { dureeSecondes: Math.max(0, duree) } : {}),
  };
}

/**
 * The fixed list of the steps of a translation (analysis, invariants, one translation per chosen
 * noyau, then the quality control, in this order), each in the state its sub-task says; a step
 * Tahiti has not created yet is "en attente", the quality control included (#32).
 *
 * The translation sub-tasks are told apart by the noyau their title names; those whose title
 * names none take, in order of creation, the noyaux nobody claimed.
 */
export function construireEtapes(
  noyaux: readonly NoyauCode[],
  sousTaches: readonly LigneSousTache[],
  types: CorrespondanceTypes,
  correspondance: CorrespondanceStatuts | undefined,
): EtapeTraduction[] {
  const duType = (nom: string) =>
    sousTaches.filter(
      (ligne) => ligne.h2_tasktype_id != null && types.get(ligne.h2_tasktype_id) === nom,
    );

  const traductions = duType(TYPE_TRADUCTION).sort((a, b) => Number(a.id) - Number(b.id));
  const parNoyau = new Map<NoyauCode, LigneSousTache[]>();
  const sansNoyau: LigneSousTache[] = [];
  for (const ligne of traductions) {
    const noyau = noyauDuTitre(ligne.title);
    if (noyau === undefined) {
      sansNoyau.push(ligne);
    } else {
      parNoyau.set(noyau, [...(parNoyau.get(noyau) ?? []), ligne]);
    }
  }
  const sansSousTache = noyaux.filter((noyau) => !parNoyau.has(noyau));
  for (const [rang, ligne] of sansNoyau.entries()) {
    const noyau = sansSousTache[rang];
    if (noyau !== undefined) parNoyau.set(noyau, [ligne]);
  }

  // The quality control (#13, #32): ONE step per launch (devdb: 910888 has six noyaux and a single
  // control, created once the last translation is done, and it covers all of them), always the
  // last of the list. It is listed from the start, "en attente", and takes the state of the
  // sub-task when Tahiti creates it (the most recent one, never a second line). It stays "en cours"
  // until the translation can be read: the sub-task ends a few seconds before the launch writes
  // its result, and every step showed "terminée" while nothing was there yet. A failure is a
  // failure. Nothing here says whether the control found anything: that is the analysis page's.
  const controle = laPlusRecente(duType(TYPE_CONTROLE));
  const etatControle: EtatEtape =
    controle === undefined ? 'attente' : etatEtape(controle.status, correspondance);

  return [
    etape(
      { code: 'analyse', libelle: 'Analyse du texte' },
      laPlusRecente(duType(TYPE_ANALYSE)),
      correspondance,
    ),
    etape(
      { code: 'invariants', libelle: 'Repérage des invariants' },
      laPlusRecente(duType(TYPE_INVARIANTS)),
      correspondance,
    ),
    ...noyaux.map((noyau) =>
      etape(
        { code: 'traduction', libelle: `Traduction ${noyau}`, noyau },
        laPlusRecente(parNoyau.get(noyau) ?? []),
        correspondance,
      ),
    ),
    {
      code: 'controle',
      libelle: 'Contrôle qualité',
      etat: etatControle === 'terminee' ? 'en_cours' : etatControle,
    },
  ];
}

/**
 * Has one of the sub-tasks of a translation failed (`I`, `F`) or been cancelled (`C`) while the
 * translation still runs (#13)? Then it is blocked: Tahiti will not end it on its own (devdb:
 * 910340 stayed "en cours" 16 h with a sub-task interrupted). Every sub-task counts, the steps
 * the screen lists and the others (a quality control, a step of a later version): for each kind
 * of sub-task (its type, and its noyau for a translation) the most recent one decides, so that a
 * step Tahiti ran again after a failure is not held against it.
 */
export function sousTacheEnEchec(
  sousTaches: readonly LigneSousTache[],
  types: CorrespondanceTypes,
  correspondance: CorrespondanceStatuts | undefined,
): boolean {
  const dernieres = new Map<string, LigneSousTache>();
  for (const ligne of sousTaches) {
    const type = ligne.h2_tasktype_id ?? '';
    const noyau = types.get(type) === TYPE_TRADUCTION ? (noyauDuTitre(ligne.title) ?? '?') : '';
    const cle = `${type}|${noyau}`;
    const connue = dernieres.get(cle);
    if (connue === undefined || Number(ligne.id) > Number(connue.id)) dernieres.set(cle, ligne);
  }
  return [...dernieres.values()].some(
    (ligne) => etatEtape(ligne.status, correspondance) === 'echec',
  );
}

/** What the reading of the sub-tasks of a running translation tells. */
export interface LectureEtapes {
  etapes: EtapeTraduction[];
  /** One of its sub-tasks failed: the translation is blocked (#13). */
  bloquee: boolean;
}

/**
 * Reads the steps of a running translation, and whether one of its sub-tasks failed (#13). Any
 * refusal or unexpected answer of Tahiti gives `undefined`, and the waiting screen then only
 * says "en cours" (decision of #7: the steps are a comfort, never a reason for the screen to
 * fail; without them a blocked translation cannot be told apart and stays "en cours"). An
 * expired session still raises.
 *
 * TO VALIDATE(#7): that the token of an end user is allowed to read `h2_task` and
 * `h2_tasktype`; if Tahiti refuses, this is the fallback every user gets.
 */
export async function lireEtapesOuRepli(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  tacheId: string,
  noyaux: readonly NoyauCode[],
  types: () => Promise<CorrespondanceTypes>,
  correspondance: CorrespondanceStatuts | undefined,
): Promise<LectureEtapes | undefined> {
  try {
    const [sousTaches, correspondanceTypes] = await Promise.all([
      lireSousTaches(appel, config, jeton, tacheId),
      types(),
    ]);
    return {
      etapes: construireEtapes(noyaux, sousTaches, correspondanceTypes, correspondance),
      bloquee: sousTacheEnEchec(sousTaches, correspondanceTypes, correspondance),
    };
  } catch (erreur) {
    if (erreur instanceof ServiceIndisponibleError) return undefined;
    throw erreur;
  }
}

/* ── Cancellation ────────────────────────────────────────────────────────────────── */

/**
 * Cancels, below the task `parentId`, every sub-task that has not ended (`W`, `R`, `S`), then
 * the sub-tasks of those, and so on: cancelling the task of a translation does not stop its
 * sub-tasks (seen on devdb, 2026-10-07: they stayed `S`), and each step has AI sub-tasks of
 * its own. A sub-task that has ended is left alone, and so is what hangs below it.
 *
 * Returns how many sub-tasks could not be stopped (a refused cancellation, or a level that
 * cannot be read: counted once): the caller says the interruption is incomplete when it is not
 * zero. An expired session raises. Nothing is read twice, whatever the shape of the tree.
 */
export async function annulerEnCascade(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  parentId: string,
  correspondance: CorrespondanceStatuts | undefined,
): Promise<number> {
  const vus = new Set<string>([parentId]);

  async function descendre(id: string): Promise<number> {
    let enfants: LigneSousTache[];
    try {
      enfants = await lireSousTaches(appel, config, jeton, id);
    } catch (erreur) {
      if (erreur instanceof ServiceIndisponibleError) return 1;
      throw erreur;
    }

    let restantes = 0;
    for (const enfant of enfants) {
      if (vus.has(enfant.id)) continue;
      vus.add(enfant.id);
      if (classerStatut(enfant.status, correspondance) !== 'en_cours') continue;
      try {
        await annulerTache(appel, config, jeton, enfant.id);
      } catch (erreur) {
        if (!(erreur instanceof ServiceIndisponibleError)) throw erreur;
        restantes += 1;
      }
      // Below a sub-task whose cancellation was refused there may be tasks still running.
      restantes += await descendre(enfant.id);
    }
    return restantes;
  }

  return descendre(parentId);
}
